import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { AnimateOnScroll } from "./AnimateOnScroll";

const solutionsData = [
  {
    id: 1,
    title: "Parking Facilities",
    description: "Accept cash or card, manage access gates, and track revenue — all from one ruggedized kiosk.",
    image: "/images/solutions/1.webp",
    link: "/industries/parking",
  },
  {
    id: 2,
    title: "24/7 Live Monitoring",
    description: "Watch every kiosk live, around the clock, from the Kinitas dashboard — machine status, sales and alerts in real time, from anywhere.",
    image: "/images/solutions/live-monitoring.webp",
    imageClass: "object-left-top",
  },
  {
    id: 4,
    title: "Revenue Analytics",
    description: "Accurate revenue charts right inside the dashboard — sales by site, product and payment type, by day, month or any custom range.",
    image: "/images/solutions/revenue-analytics.webp",
    imageClass: "object-left-top",
  },
  {
    id: 5,
    title: "Carwash",
    description: "Run a fully automated car wash — no attendant needed. Accept cash or card, sell memberships, and grow your customer list on autopilot.",
    image: "/images/solutions/2.webp",
    link: "/industries/car-wash",
  },
];

export const Solutions = () => {
  const scrollRef = useRef(null);
  const [canScroll, setCanScroll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkOverflow = () => {
      if (scrollRef.current) {
        setCanScroll(scrollRef.current.scrollWidth > scrollRef.current.clientWidth + 1);
      }
    };

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    if (scrollRef.current) observer.observe(scrollRef.current);

    return () => observer.disconnect();
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -300 : 300,
        behavior: "smooth",
      });
    }
  };

  return (
    <section
      id="solutions"
      className="relative py-20 px-6 lg:px-8 bg-[#f5f6f8]"
      style={{ overflow: "hidden" }}
    >
      {/* Background shape */}
      <div className="absolute top-0 right-0 w-full h-full z-0 pointer-events-none">
        <img
          src="/images/s2-bg.svg"
          alt=""
          className="absolute top-[-80px] right-[-250px] w-[500px] lg:w-[600px] opacity-80"
        />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto">
        <AnimateOnScroll>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center text-[#111827] mb-14 leading-tight">
            Custom-Built Kiosk <br className="hidden sm:block" /> Solutions for Every Industry
          </h2>
        </AnimateOnScroll>

        {/* Scroll container */}
        <div
          ref={scrollRef}
          className="snap-x snap-mandatory scroll-smooth hide-scrollbar"
          style={{
            overflowX: "auto",
            overflowY: "clip",
            paddingTop: "8px",
            paddingBottom: "36px",
          }}
        >
          {/* Inner row — centers when cards fit, scrolls when they don't */}
          <div className="inline-flex gap-6 min-w-full justify-center">
            {solutionsData.map((item, index) => (
              <motion.div
                key={item.id}
                className={`snap-start flex-shrink-0 w-[270px] sm:w-[280px] bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300${item.link ? " cursor-pointer" : ""}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                onClick={item.link ? () => navigate(item.link) : undefined}
                role={item.link ? "link" : undefined}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  className={`w-full h-[270px] object-cover ${item.imageClass || ""}`}
                />
                <div className="p-5">
                  <h3 className="text-[#111827] font-semibold text-lg mb-2">
                    {item.title}
                  </h3>
                  <p className="text-[#6b7280] text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Navigation buttons — only visible when cards don't fit */}
        {canScroll && (
          <div className="flex justify-end gap-3 mt-8">
            <button
              onClick={() => scroll("left")}
              className="w-10 h-10 rounded-lg bg-[#1a1f6c] text-white flex items-center justify-center hover:bg-[#2832c2] transition-colors duration-300"
              aria-label="Scroll left"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => scroll("right")}
              className="w-10 h-10 rounded-lg bg-[#1a1f6c] text-white flex items-center justify-center hover:bg-[#2832c2] transition-colors duration-300"
              aria-label="Scroll right"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};